import { useCallback, useEffect, useMemo, useState } from "react";
import { actualizar, rpc } from "../../services/datos";
import DetalleReunion from "./DetalleReunion";
import { antetitulo, botonPequeno, botonPrimario, fechaLarga, hora, nombreReunion } from "../../shared/estilos";
import { avisar } from "../../shared/avisos";
import Esqueleto from "../../shared/components/Esqueleto";

// Cada minuto, y solo con la pestaña a la vista: con muchos servidores conectados a la
// vez, recargar seguido y en segundo plano es lo que más consultas genera.
const RECARGA_MS = 60000;

function estadoDe(turno) {
  if (turno.rechazado) return { texto: "No puedes asistir", clase: "border-red-900 text-red-300" };
  if (turno.confirmado) return { texto: "Confirmado", clase: "border-green-900 text-green-300" };
  return { texto: "Por confirmar", clase: "border-yellow-900 text-yellow-300" };
}

// Turnos de quien entra: los próximos en orden de fecha y, para cada reunión, con
// quién sirve y el programa. La usan la vista de servidor y el menú de los demás roles.
function MisTurnos({ usuario, avisosActivos = false, estadoPush = "" }) {
  const [turnos, setTurnos] = useState([]);
  const [historial, setHistorial] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState("");
  const [tab, setTab] = useState("proximos");
  const [abierto, setAbierto] = useState(null);
  const [procesando, setProcesando] = useState(null);
  const miembroId = usuario?.miembro?.id ?? usuario?.miembro_id;

  const cargarDatos = useCallback(async () => {
    if (!miembroId) {
      setCargando(false);
      return;
    }
    try {
      // Al abrir sus turnos, la base los marca como vistos para el equipo de coordinación.
      const [mios, archivo] = await Promise.all([rpc("mis_turnos"), rpc("historial_turnos", { p_reuniones: 1 })]);
      setTurnos(Array.isArray(mios) ? mios : []);
      setHistorial(Array.isArray(archivo) ? archivo : []);
      setError("");
    } catch (e) {
      setError(e.message || "No se pudieron cargar tus turnos");
    } finally {
      setCargando(false);
    }
  }, [miembroId]);

  useEffect(() => {
    cargarDatos();
    const recargarSiSeVe = () => document.visibilityState !== "hidden" && cargarDatos();
    const intervalo = window.setInterval(recargarSiSeVe, RECARGA_MS);
    window.addEventListener("focus", cargarDatos);
    return () => {
      window.clearInterval(intervalo);
      window.removeEventListener("focus", cargarDatos);
    };
  }, [cargarDatos]);

  const proximos = useMemo(() => [...turnos].sort((a, b) =>
    `${a.servicios?.fecha || ""}${a.servicios?.hora || ""}`.localeCompare(`${b.servicios?.fecha || ""}${b.servicios?.hora || ""}`)
  ), [turnos]);

  const responder = async (turno, cambios) => {
    setProcesando(turno.id);
    try {
      await actualizar("turnos_servicio", turno.id, cambios);
      await cargarDatos();
    } catch (e) {
      avisar(e.message || "No se pudo actualizar el turno", "error");
    } finally {
      setProcesando(null);
    }
  };

  const noPuedo = (turno) => {
    const motivo = window.prompt("¿Por qué no puedes cumplir este turno? Se avisará a los pastores para buscar un reemplazo.");
    if (motivo !== null) responder(turno, { rechazado: true, motivo_rechazo: motivo });
  };

  // Solo la reunión más reciente del historial, agrupada por área.
  const ultimaReunion = useMemo(() => historial.filter((r) =>
    r.servicio_id === historial[0].servicio_id && r.fecha_servicio === historial[0].fecha_servicio
  ).reduce((grupos, registro) => {
    const area = registro.nombre_area || "Sin área";
    (grupos[area] = grupos[area] || []).push(registro);
    return grupos;
  }, {}), [historial]);

  return (
    <>
      <p className={antetitulo}>Hola, {usuario.miembro?.nombres || usuario.username}</p>
      <h1 className="titulo-pagina">Mis turnos</h1>
      <p className="mt-2 text-sm text-gray-400">Confirma cada turno o avisa si no puedes. Abre la reunión para ver con quién sirves y el programa.</p>
      {estadoPush && <p className="mt-3 text-sm text-gray-300">{estadoPush}</p>}

      {!miembroId && (
        <p role="alert" className="mt-6 rounded border border-yellow-900 bg-yellow-950/30 p-4 text-sm text-yellow-200">
          Tu usuario no está vinculado a un miembro. Pide a un pastor que lo vincule para ver tus turnos.
        </p>
      )}
      {error && <p role="alert" className="rounded-xl border border-rose-500/30 bg-rose-500/10 text-rose-200 mt-6 p-4 text-sm">{error}</p>}

      <div className="mt-6 flex gap-2">
        {[["proximos", "Próximos"], ["historial", "Última reunión"]].map(([id, texto]) => (
          <button key={id} onClick={() => setTab(id)} aria-pressed={tab === id}
            className={`pestana ${tab === id ? "pestana-activa" : ""}`}>
            {texto}
          </button>
        ))}
      </div>

      {cargando ? (
        <Esqueleto className="mt-8" cantidad={3} texto="Cargando tus turnos…" />
      ) : tab === "proximos" ? (
        proximos.length === 0 ? (
          <div className="mt-6 rounded-2xl border border-dashed border-gray-700 p-12 text-center">
            <p className="font-semibold text-white">No tienes turnos programados</p>
            <p className="mt-2 text-sm text-gray-400">Cuando te asignen uno aparecerá aquí{avisosActivos ? " y te llegará un aviso" : ""}.</p>
          </div>
        ) : (
          <ul className="mt-6 space-y-4">
            {proximos.map((turno) => {
              const servicio = turno.servicios || {};
              const estado = estadoDe(turno);
              const ocupado = procesando === turno.id;
              return (
                <li key={turno.id} className="panel p-5">
                  <div className="flex flex-col gap-4 sm:flex-row">
                    <div className="w-16 shrink-0 text-center">
                      <p className="text-3xl font-bold leading-none">{servicio.fecha ? Number(servicio.fecha.slice(8, 10)) : "—"}</p>
                      <p className="mt-1 text-gray-400 text-xs">
                        {fechaLarga(servicio.fecha, { month: "short" })}
                      </p>
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-start justify-between gap-2">
                        <div>
                          <p className="text-white font-semibold">{nombreReunion(servicio)}</p>
                          <p className="text-sm capitalize text-gray-400">
                            {fechaLarga(servicio.fecha)}{servicio.hora ? ` · ${hora(servicio.hora)}` : ""}
                          </p>
                        </div>
                        <span className={`rounded-full border px-3 py-1 text-xs font-semibold ${estado.clase}`}>{estado.texto}</span>
                      </div>
                      <p className="mt-3 text-sm text-white">
                        {turno.areas_servicio?.nombre || "Área por definir"}{turno.rol_en_turno ? ` · ${turno.rol_en_turno}` : ""}
                      </p>
                      {turno.notas && <p className="mt-1 text-sm text-gray-400">{turno.notas}</p>}
                      {turno.rechazado && (
                        <p className="mt-2 text-sm text-red-300">Avisaste que no puedes. Motivo: {turno.motivo_rechazo || "no indicaste un motivo"}.</p>
                      )}

                      <div className="mt-4 flex flex-wrap gap-2">
                        {!turno.confirmado && (
                          <button onClick={() => responder(turno, { confirmado: true })} disabled={ocupado} className={botonPrimario}>
                            {ocupado ? "Guardando..." : turno.rechazado ? "Sí puedo asistir" : "Confirmar"}
                          </button>
                        )}
                        {!turno.rechazado && (
                          <button onClick={() => noPuedo(turno)} disabled={ocupado} className={botonPequeno}>No puedo asistir</button>
                        )}
                        {turno.confirmado && (
                          <button onClick={() => responder(turno, { confirmado: false })} disabled={ocupado} className={botonPequeno}>Marcar pendiente</button>
                        )}
                        <button onClick={() => setAbierto(abierto === turno.id ? null : turno.id)} aria-expanded={abierto === turno.id} className={botonPequeno}>
                          {abierto === turno.id ? "Ocultar reunión" : "Equipo y programa"}
                        </button>
                      </div>
                      {abierto === turno.id && <DetalleReunion servicioId={turno.servicio_id} />}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )
      ) : historial.length === 0 ? (
        <p className="mt-6 text-sm text-gray-400">Aún no hay reuniones archivadas.</p>
      ) : (
        <div className="mt-6 space-y-3">
          <p className="text-sm text-gray-400">
            {nombreReunion({ titulo: historial[0].titulo_servicio || historial[0].tipo_servicio })} · {fechaLarga(historial[0].fecha_servicio)}
          </p>
          {Object.entries(ultimaReunion).map(([area, registros]) => (
            <section key={area} className="panel p-4">
              <p className="text-white font-bold text-sm">{area}</p>
              <ul className="mt-2 space-y-1 text-sm">
                {registros.map((r) => (
                  <li key={r.id} className="flex justify-between gap-3">
                    <span className={r.miembro_id === miembroId ? "font-semibold text-white" : "text-gray-300"}>
                      {r.nombre_servidor}{r.rol_en_turno ? ` · ${r.rol_en_turno}` : ""}
                    </span>
                    <span className={`text-xs ${r.confirmado ? "text-green-300" : "text-gray-500"}`}>{r.confirmado ? "Confirmó" : "No confirmó"}</span>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
      )}
    </>
  );
}

export default MisTurnos;
